import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Tabs, Dropdown, Input, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../store';
import {
  getRiskAssessmentProcesses,
  parseAndGroupRiskAssessments,
  RiskAssessment,
  normalizeRiskRanking,
  type CompliancePurpose,
} from '../../../services/riskAssessmentService';
import {
  setRiskAssessmentCycles,
} from '../../../store/slices/riskAssessmentSlice';
import { updateProcessRiskRanking } from '../../../store/slices/hierarchySlice';
import { useAssessmentDetails } from '../../../hooks/useAssessmentDetails';
import ExportButton from '../../../components/common/ExportButton';
import ExportPanel, { ExportColumnConfig } from '../../../components/common/ExportPanel';
import ChevronDownIcon from '../../../assets/icons/ic-chevron-down.svg?react';
import ChevronRightIcon from '../../../assets/icons/ic-chevron-right.svg?react';
import RankingIcon from '../../../assets/icons/ic-ranking.svg?react';
import ObjectivesIcon from '../../../assets/icons/ic-objectives.svg?react';
import AlertIcon from '../../../assets/icons/ic-alert.svg?react';
import MatrixIcon from '../../../assets/icons/ic-matrix.svg?react';
// Tab Components
import Tab1ProcessLevelRiskRanking from './tab1/Tab1ProcessLevelRiskRanking';
import Tab2ObjectivesAndRisks from './tab2/Tab2ObjectivesAndRisks';
import Tab3FraudRisk from './tab3/Tab3FraudRisk';
import Tab4RiskMatrixConfiguration from './tab4/Tab4RiskMatrixConfiguration';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  gap: 24px !important;
`;

const PageTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const SelectorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6B7280;

  &:hover {
    background: #E5E7EB;
    color: #1F2937;
  }

  &:active {
    background: #D1D5DB;
  }

  .anticon {
    font-size: 16px;
  }
`;

const CycleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;

const CycleText = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
`;

const ChevronIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color || '#6B7280'};
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  svg path {
    stroke: ${props => props.$color || '#6B7280'};
  }
`;

const ProcessSelectorsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProcessChipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProcessChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 28px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
  }
`;

const ProcessOwnerChip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
  height: 38px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 18px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  margin-left: auto;
`;

// Dropdown styles
const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 346px;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 15px;
  }
`;

const DropdownSection = styled.div`
  margin-bottom: 8px;
`;

const DropdownSectionLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  padding: 8px 12px 8px 12px;
`;

// Cycle dropdown item (simple style)
const CycleDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: #F3F4F6;
  }
`;

const CycleDropdownItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  flex: 1;
`;

const CycleDropdownItemIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Process dropdown item (pill chip style)
const ProcessDropdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 4px 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProcessDropdownItemChip = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  height: 28px;
  border-radius: 50px;
  background: ${props => props.$selected ? '#2563EB' : '#EFF6FF'};
  transition: all 0.3s;

  &:hover {
    opacity: 0.9;
  }
`;

const ProcessDropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: ${props => props.$selected ? '#FFFFFF' : '#1E40AF'};
  flex: 1;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

// const ActionMenu = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 10px;
//   margin-bottom: 10px;
// `;

// const ActionItem = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 46px;
//   height: 42px;
//   border: 1.5px solid #2563EB;
//   border-radius: 8px;
//   cursor: pointer;
//   transition: all 0.3s;
//   background: white;

//   svg {
//     width: 14px;
//     height: 14px;
//   }

//   svg path {
//     stroke: #2563EB;
//   }

//   &:hover {
//     background: #EFF6FF;
//   }

//   &:active {
//     background: #DBEAFE;
//   }
// `;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ==================== TYPES ====================

// ==================== MAIN COMPONENT ====================

// Helper function to check if a string is a UUID
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const RiskAssessmentDetailPage: React.FC = () => {
  const { id: assessmentIdOrCode, tab } = useParams<{ id: string; tab?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { defaultTab?: string } | null;

  // Get Redux data first (needed for code lookup)
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { 
    cycles: riskAssessmentCycles, 
    lastFetchedReportId: lastFetchedRiskAssessmentReportId,
    currentAssessmentDetails,
    objectives,
    compliancePurposes,
  } = useSelector((state: RootState) => state.riskAssessment);

  // Determine if param is UUID or code, and resolve to actual assessmentId
  const resolvedAssessmentId = useMemo(() => {
    if (!assessmentIdOrCode) return undefined;
    
    // If it's a UUID, use it directly
    if (isUUID(assessmentIdOrCode)) {
      return assessmentIdOrCode;
    }
    
    // Otherwise, it's a code (like A01, A02) - find by processCode
    // processCode always equals assessmentCode for a RiskAssessment
    for (const cycle of riskAssessmentCycles) {
      const assessment = cycle.riskAssessments.find(
        ra => ra.processCode.toLowerCase() === assessmentIdOrCode.toLowerCase()
      );
      if (assessment) {
        return assessment.id;
      }
    }
    
    // If not found yet (cycles might not be loaded), return undefined
    return undefined;
  }, [assessmentIdOrCode, riskAssessmentCycles]);

  // Redirect to actual assessmentId URL if we resolved from code
  useEffect(() => {
    if (
      assessmentIdOrCode && 
      !isUUID(assessmentIdOrCode) && 
      resolvedAssessmentId && 
      riskAssessmentCycles.length > 0
    ) {
      // Replace URL with actual assessmentId
      navigate(`/risk-assessment/${resolvedAssessmentId}/${tab || 'tab1'}`, {
        replace: true,
      });
    }
  }, [assessmentIdOrCode, resolvedAssessmentId, tab, navigate, riskAssessmentCycles.length]);

  // Use assessment details hook with resolved ID
  const { isLoading: isLoadingAssessmentData } = useAssessmentDetails(resolvedAssessmentId);

  // Determine initial active tab from URL or state or default
  const getInitialTab = () => {
    if (tab && ['tab1', 'tab2', 'tab3', 'tab4'].includes(tab)) {
      return tab;
    }
    if (state?.defaultTab) {
      return state.defaultTab;
    }
    return 'tab1';
  };

  // State
  const [isLoadingCycles, setIsLoadingCycles] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [cycleSearchTerm, setCycleSearchTerm] = useState('');
  const [processSearchTerm, setProcessSearchTerm] = useState('');
  const [exportPanelOpen, setExportPanelOpen] = useState(false);

  // Sync activeTab with URL param when navigating between assessments
  useEffect(() => {
    if (tab && ['tab1', 'tab2', 'tab3', 'tab4'].includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Ref to prevent duplicate fetches
  const hasFetchedRiskAssessments = useRef(false);

  // Fetch risk assessment cycles if not in Redux
  useEffect(() => {
    const fetchRiskAssessmentsData = async () => {
      if (!selectedReport?.annualReportId) {
        setIsLoadingCycles(false);
        return;
      }

      const hasCachedData = lastFetchedRiskAssessmentReportId === selectedReport.annualReportId;
      if (hasCachedData && riskAssessmentCycles.length > 0) {
        setIsLoadingCycles(false);
        return;
      }

      if (hasFetchedRiskAssessments.current) return;
      hasFetchedRiskAssessments.current = true;

      // console.log('🔄 Fetching risk assessment cycles...');
      setIsLoadingCycles(true);

      try {
        const response = await getRiskAssessmentProcesses(selectedReport.annualReportId);
        const parsedCycles = parseAndGroupRiskAssessments(response.data);
        
        dispatch(setRiskAssessmentCycles({
          cycles: parsedCycles,
          reportId: selectedReport.annualReportId,
        }));

        if (response.data.filter(p => p.assessmentId == resolvedAssessmentId).length === 0) {
          
          const newAssessmentId = response.data.filter(p => p.processName.substring(0, 3) == currentAssessmentData?.processCode).length != 0 
              ? response.data.filter(p => p.processName.substring(0, 3) === currentAssessmentData?.processCode)[0].assessmentId
              : currentAssessmentData?.processCode;

          hasFetchedRiskAssessments.current = false;
          
          navigate(`/risk-assessment/${newAssessmentId}/${tab || 'tab1'}`);
        }
        
        // console.log(`✅ Fetched ${parsedCycles.length} risk assessment cycles`);
      } catch (error: any) {
        console.error('❌ Failed to fetch risk assessment cycles:', error);
        message.error('Failed to load risk assessment data');
        hasFetchedRiskAssessments.current = false;
      } finally {
        setIsLoadingCycles(false);
      }
    };

    fetchRiskAssessmentsData();
  }, [selectedReport?.annualReportId, lastFetchedRiskAssessmentReportId, riskAssessmentCycles.length, dispatch]);

  // Get current assessment data from Redux (by resolvedAssessmentId)
  const currentAssessmentData = useMemo<RiskAssessment | null>(() => {
    if (!resolvedAssessmentId || riskAssessmentCycles.length === 0) return null;

    // Find assessment by ID across all cycles
    for (const cycle of riskAssessmentCycles) {
      const assessment = cycle.riskAssessments.find(ra => ra.id === resolvedAssessmentId);
      if (assessment) return assessment;
    }
    return null;
  }, [resolvedAssessmentId, riskAssessmentCycles]);

  // Sync risk ranking with hierarchySlice (Process <-> RiskAssessment 1:1 mapping)
  useEffect(() => {
    if (
      currentAssessmentData?.processCode &&
      currentAssessmentData?.cycleCode &&
      currentAssessmentDetails?.overallRiskRanking
    ) {
      dispatch(updateProcessRiskRanking({
        cycleCode: currentAssessmentData.cycleCode,
        processCode: currentAssessmentData.processCode,
        riskRanking: currentAssessmentDetails.overallRiskRanking,
      }));
    }
  }, [
    currentAssessmentData?.processCode,
    currentAssessmentData?.cycleCode,
    currentAssessmentDetails?.overallRiskRanking,
    dispatch,
  ]);

  // Get selected cycle (from risk assessment cycles)
  const selectedCycle = useMemo(() => {
    if (!currentAssessmentData) return null;
    
    return riskAssessmentCycles.find(
      c => c.cycleCode === currentAssessmentData.cycleCode
    );
  }, [currentAssessmentData, riskAssessmentCycles]);

  // Get available processes for selected cycle (from risk assessment cycles)
  const availableProcesses = useMemo(() => {
    if (!selectedCycle) return [];
    return selectedCycle.riskAssessments;
  }, [selectedCycle]);

  // Filter cycles for search (from risk assessment cycles)
  const filteredCycles = useMemo(() => {
    if (!cycleSearchTerm) return riskAssessmentCycles;
    const term = cycleSearchTerm.toLowerCase();
    return riskAssessmentCycles.filter(cycle =>
      cycle.cycleCode.toLowerCase().includes(term) ||
      cycle.cycleName.toLowerCase().includes(term)
    );
  }, [cycleSearchTerm, riskAssessmentCycles]);

  // Filter processes for search (risk assessments)
  const filteredProcesses = useMemo(() => {
    if (!processSearchTerm) return availableProcesses;
    const term = processSearchTerm.toLowerCase();
    return availableProcesses.filter(assessment =>
      assessment.processCode.toLowerCase().includes(term) ||
      assessment.processName.toLowerCase().includes(term)
    );
  }, [processSearchTerm, availableProcesses]);


  const objectivesTemplate = (props: CompliancePurpose) => {
    if (!props.objectiveNumbers || props.objectiveNumbers.length === 0) {
      return '';
    }

    // Group objectives by process code
    const grouped: Record<string, string[]> = {};
    props.objectiveNumbers.forEach((num, idx) => {
      const processCode = props.objectiveProcessCodes?.[idx] || '?';
      if (!grouped[processCode]) grouped[processCode] = [];
      grouped[processCode].push(num);
    });

    const groupEntries = Object.entries(grouped);

    const renderChips = () => groupEntries.map(([processCode, numbers]) => {

      return (
        
        `${processCode}: ${[...numbers]
              .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .map((num) => (
                `${num}`
              )).join(", ")}`
    );
    })
    const finalString: string = renderChips().join('\n');

    return (
          finalString
    );
  };

  // Export: shared metadata (Cycle, Process, Process Owner)
  const { manualQualitativeFactors } = useSelector((state: RootState) => state.riskAssessment);

  const baseMetadataRows = useMemo(() => {
    const cycleLabel = selectedCycle
      ? `${selectedCycle.cycleCode} - ${selectedCycle.cycleName}`
      : '-';
    const processLabel = currentAssessmentData
      ? `${currentAssessmentData.processCode} ${currentAssessmentData.processName}`
      : '-';
    const processOwner = currentAssessmentData?.processOwnerName || '-';

    return [
      { label: 'Cycle', value: cycleLabel },
      { label: 'Process', value: processLabel },
      { label: 'Process Owner', value: processOwner },
    ];
  }, [selectedCycle, currentAssessmentData]);

  // Export config: dynamic based on active tab
  const { exportMetadataRows, exportColumns, exportData, exportFileName, exportTitle } = useMemo((): {
    exportMetadataRows: { label: string; value: string }[];
    exportColumns: ExportColumnConfig[];
    exportData: Record<string, string>[];
    exportFileName: string;
    exportTitle: string;
  } => {
    if (activeTab === 'tab2') {
      return {
        exportMetadataRows: baseMetadataRows,
        exportColumns: [
          { key: 'index', title: '#', width: 5 },
          { key: 'objective', title: 'Objective', width: 25 },
          { key: 'risk', title: 'Risk', width: 25 },
          { key: 'riskRanking', title: 'Objective Risk Ranking', width: 15 },
          { key: 'assertions', title: 'Assertions', width: 15 },
          { key: 'controls', title: 'Controls', width: 15 },
        ] as ExportColumnConfig[],
        exportData: objectives.map((obj, idx) => ({
          index: String(idx + 1).padStart(2, '0'),
          objective: obj.objectives,
          risk: obj.risks,
          riskRanking: obj.controlRiskRanking,
          assertions: obj.financialStatementAssertions?.join(', ') || '',
          controls: obj.riskMitigatingControls?.join(', ') || obj.controlText || '',
        })),
        exportFileName: 'ObjectivesAndRisks',
        exportTitle: 'Objectives and Risks',
      };
    }

    if (activeTab === 'tab3') {
      return {
        exportMetadataRows: [
          ...baseMetadataRows,
          { label: 'Incentives & Pressures', value: currentAssessmentDetails?.incentives || '-' },
          { label: 'Opportunities', value: currentAssessmentDetails?.opportunities || '-' },
          { label: 'Rationalizations & Attitude', value: currentAssessmentDetails?.rationalizations || '-' },
        ],
        exportColumns: [
          { key: 'index', title: '#', width: 5 },
          { key: 'specificFraudRiskFactors', title: 'Specific Fraud Risk Factors', width: 35 },
          { key: 'objectiveNumbers', title: 'Objectives', width: 20 },
          { key: 'controlCodes', title: 'Fraud Risk Mitigating Controls', width: 25 },
        ] as ExportColumnConfig[],
        exportData: compliancePurposes.map((item, idx) => ({
          index: String(idx + 1).padStart(2, '0'),
          specificFraudRiskFactors: item.specificFraudRiskFactors,
          objectiveNumbers: objectivesTemplate(item),
          controlCodes: item.controlCodes?.join(', ') || '',
        })),
        exportFileName: 'FraudRisk',
        exportTitle: 'Fraud Risk',
      };
    }

    // Default: Tab 1 - Process-Level Risk Ranking
    return {
      exportMetadataRows: [
        ...baseMetadataRows,
        { label: 'Quantitative Assessment', value: currentAssessmentDetails?.quantitativeAssessment || '-' },
      ],
      exportColumns: [
        { key: 'qualitativeFactor', title: 'Qualitative Factor', width: 25 },
        { key: 'riskRanking', title: 'Risk Ranking', width: 15 },
        { key: 'comment', title: 'Comment', width: 40 },
      ] as ExportColumnConfig[],
      exportData: [
        ...(currentAssessmentDetails ? [{
          qualitativeFactor: 'Fraud Risk',
          riskRanking: normalizeRiskRanking(currentAssessmentDetails.fraudRiskRanking) as string,
          comment: currentAssessmentDetails.fraudRiskDescription || '',
        }] : []),
        ...manualQualitativeFactors.map(f => ({
          qualitativeFactor: f.qualitativeFactor,
          riskRanking: f.riskRanking as string,
          comment: f.customComment || '',
        })),
        ...(currentAssessmentDetails ? [{
          qualitativeFactor: 'Overall Risk Ranking Conclusion',
          riskRanking: normalizeRiskRanking(currentAssessmentDetails.overallRiskRanking) as string,
          comment: currentAssessmentDetails.overallRiskDescription || '',
        }] : []),
      ],
      exportFileName: 'ProcessLevelRiskRanking',
      exportTitle: 'Process-Level Risk Ranking',
    };
  }, [activeTab, baseMetadataRows, currentAssessmentDetails, manualQualitativeFactors, objectives, compliancePurposes]);

  // Handle cycle selection - auto-select first process and navigate
  const handleCycleSelect = (cycleId: string) => {
    setCycleSearchTerm('');
    
    // Find the selected cycle
    const cycle = riskAssessmentCycles.find(c => c.id === cycleId);
    if (!cycle || cycle.riskAssessments.length === 0) return;

    // Auto-select first process and navigate
    const firstAssessment = cycle.riskAssessments[0];
    // console.log('🔄 Auto-selecting first process:', firstAssessment.processCode);
    handleProcessSelect(firstAssessment.id);
  };

  // Handle process selection - navigate to new assessment page
  const handleProcessSelect = (assessmentId: string) => {
    setProcessSearchTerm('');
    
    // console.log('✅ Navigating to assessment:', assessmentId);
    
    // Navigate to new assessment page with current tab in URL
    navigate(`/risk-assessment/${assessmentId}/${activeTab}`, { 
      replace: true, // Replace current history entry
    });
  };

  // Handle tab change - update URL with new tab
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    
    // Update URL with new tab (without full page reload)
    if (resolvedAssessmentId) {
      navigate(`/risk-assessment/${resolvedAssessmentId}/${newTab}`, {
        replace: true, // Replace current history entry
      });
    }
  };

  // Cycle dropdown menu
  const cycleDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for cycle"
        value={cycleSearchTerm}
        onChange={(e) => setCycleSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {cycleSearchTerm === '' && (
          <DropdownSectionLabel>All cycles</DropdownSectionLabel>
        )}
        {filteredCycles.map(cycle => {
          const isSelected = selectedCycle?.id === cycle.id;
          return (
            <CycleDropdownItem
              key={cycle.id}
              $selected={isSelected}
              onClick={() => handleCycleSelect(cycle.id)}
            >
              <CycleDropdownItemText>
                {cycle.cycleCode} {cycle.cycleName}
              </CycleDropdownItemText>
              <CycleDropdownItemIcon>
                <ChevronRightIcon />
              </CycleDropdownItemIcon>
            </CycleDropdownItem>
          );
        })}
      </DropdownSection>
    </DropdownMenu>
  );

  // Process dropdown menu
  const processDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        placeholder="Search for process"
        value={processSearchTerm}
        onChange={(e) => setProcessSearchTerm(e.target.value)}
        autoFocus
      />
      <DropdownSection>
        {processSearchTerm === '' && (
          <DropdownSectionLabel>All processes</DropdownSectionLabel>
        )}
        {filteredProcesses.map(assessment => {
          const isSelected = currentAssessmentData?.id === assessment.id;
          return (
            <ProcessDropdownItem key={assessment.id} onClick={() => !isSelected && handleProcessSelect(assessment.id)}>
              <ProcessDropdownItemChip $selected={isSelected}>
                <ProcessDropdownItemText $selected={isSelected}>
                  {assessment.processCode} {assessment.processName}
                </ProcessDropdownItemText>
              </ProcessDropdownItemChip>
            </ProcessDropdownItem>
          );
        })}
      </DropdownSection>
    </DropdownMenu>
  );

  const tabItems = [
    {
      key: 'tab1',
      label: (
        <TabLabel>
          <RankingIcon />
          Process-Level Risk Rankings
        </TabLabel>
      ),
      children: <Tab1ProcessLevelRiskRanking />,
    },
    {
      key: 'tab2',
      label: (
        <TabLabel>
          <ObjectivesIcon />
          Objectives and Risks
        </TabLabel>
      ),
      children: <Tab2ObjectivesAndRisks />,
    },
    {
      key: 'tab3',
      label: (
        <TabLabel>
          <AlertIcon />
          Fraud Risks
        </TabLabel>
      ),
      children: <Tab3FraudRisk />,
    },
    {
      key: 'tab4',
      label: (
        <TabLabel>
          <MatrixIcon />
          Risk Matrix Configuration
        </TabLabel>
      ),
      children: <Tab4RiskMatrixConfiguration />,
    },
  ];

  // Loading state (show if either cycles or assessment data is loading)
  if (isLoadingCycles || isLoadingAssessmentData) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR Risk Assessment</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading risk assessment data...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>ICFR Risk Assessment</PageTitle>

        <div style={{ height: '20px' }}></div>

        <SelectorsSection>
          {/* Cycle Selector Row */}
          <SelectorRow>
            <BackButton onClick={() => navigate('/risk-assessment')} title="Back to Risk Assessment List">
              <LeftOutlined />
            </BackButton>
            <Dropdown
              dropdownRender={() => cycleDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
            >
              <CycleSelector>
                <CycleText>
                  Cycle: {selectedCycle ? `${selectedCycle.cycleCode} - ${selectedCycle.cycleName}` : 'Select cycle'}
                </CycleText>
                <ChevronIcon $color="#1F2937">
                  <ChevronDownIcon />
                </ChevronIcon>
              </CycleSelector>
            </Dropdown>
            {activeTab !== 'tab4' && (
              <div style={{ marginLeft: 'auto' }}>
                <ExportButton onClick={() => setExportPanelOpen(true)} />
              </div>
            )}
          </SelectorRow>

          {/* Process Chip Row */}
          <SelectorRow>
            <Dropdown
              dropdownRender={() => processDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
             >
               <ProcessSelectorsWrapper>
                 {currentAssessmentData && (
                   <ProcessChipWrapper>
                     <ProcessChip onClick={() => { }}>
                       {currentAssessmentData.processCode} {currentAssessmentData.processName}
                     </ProcessChip>
                   </ProcessChipWrapper>
                 )}
                 <ChevronIcon $color="#6B7280">
                   <ChevronDownIcon />
                 </ChevronIcon>
               </ProcessSelectorsWrapper>
             </Dropdown>

            {/* Process Owner Chip */}
            {currentAssessmentData?.processOwnerName && (
              <ProcessOwnerChip>
                Process Owner: {currentAssessmentData.processOwnerName}
              </ProcessOwnerChip>
            )}
          </SelectorRow>
        </SelectorsSection>
      </PageHeader>

      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      <ExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
        data={exportData}
        columns={exportColumns}
        fileName={exportFileName}
        title={exportTitle}
        formats={['excel', 'pdf']}
        metadataRows={exportMetadataRows}
      />
    </PageContainer>
  );
};

export default RiskAssessmentDetailPage;

